import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdCurrencyRupee,
  MdInventory,
  MdTrendingUp,
  MdTrendingDown,
  MdReceiptLong,
  MdAccountBalance,
  MdPercent,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import { getData } from '../../Webservice/Webservice';
import { FONT, inr, toISO, authToken, rangeLabel, StatCard, statGridSx, exportButtonSx, downloadCsv, openPrintView } from './reportShared';

const REPORT_ROUTE = 'reports/profit-loss';

// Indian financial periods: quarters and FY start in April.
const periodRange = (key) => {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth();
  const qStartMonth = (Math.floor(((m + 9) % 12) / 3) * 3 + 3) % 12;
  const qStartYear = m < 3 && qStartMonth >= 3 ? y - 1 : y;
  const fyStart = m >= 3 ? y : y - 1;
  switch (key) {
    case 'this_month':
      return { from: toISO(new Date(y, m, 1)), to: toISO(t) };
    case 'last_month':
      return { from: toISO(new Date(y, m - 1, 1)), to: toISO(new Date(y, m, 0)) };
    case 'this_quarter':
      return { from: toISO(new Date(qStartYear, qStartMonth, 1)), to: toISO(t) };
    case 'last_quarter':
      return { from: toISO(new Date(qStartYear, qStartMonth - 3, 1)), to: toISO(new Date(qStartYear, qStartMonth, 0)) };
    case 'this_fy':
      return { from: toISO(new Date(fyStart, 3, 1)), to: toISO(t) };
    case 'last_fy':
      return { from: toISO(new Date(fyStart - 1, 3, 1)), to: toISO(new Date(fyStart, 2, 31)) };
    default:
      return { from: '', to: '' };
  }
};

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'this_fy', label: 'This FY' },
  { key: 'last_fy', label: 'Last FY' },
  { key: 'all', label: 'All time' },
];

const signedInr = (n) => (n < 0 ? `(${inr(Math.abs(n))})` : inr(n));
const profitColor = (n) => (n > 0 ? '#15803d' : n < 0 ? '#b91c1c' : '#0f172a');

const STATEMENT_STYLE = {
  header: { background: '#0f172a', color: '#fff', fontWeight: 800, fontSize: 13.5 },
  line: { background: 'transparent', color: '#334155', fontWeight: 500, fontSize: 13 },
  subtotal: { background: '#f1f5f9', color: '#0f172a', fontWeight: 700, fontSize: 13 },
  total: { background: '#fef9c3', color: '#0f172a', fontWeight: 800, fontSize: 14 },
};

const MONTH_COLUMNS = [
  { key: 'month', label: 'Month' },
  { key: 'netRevenue', label: 'Net Revenue', money: true },
  { key: 'cogs', label: 'COGS', money: true },
  { key: 'grossProfit', label: 'Gross Profit', money: true, colored: true },
  { key: 'opex', label: 'Operating Expenses', money: true },
  { key: 'netProfit', label: 'Net Profit', money: true, colored: true },
  { key: 'netMarginPct', label: 'Net Margin', pct: true, colored: true },
];

const ProfitLossReport = () => {
  const initial = periodRange('this_fy');
  const [period, setPeriod] = useState('this_fy');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [from, to]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, params, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchReport();
    setPage(0);
  }, [fetchReport]);

  const applyPeriod = (key) => {
    const r = periodRange(key);
    setPeriod(key);
    setFrom(r.from);
    setTo(r.to);
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPeriod('');
  };

  const stats = data?.stats || {};
  const statement = data?.statement || [];
  const monthly = data?.monthly || [];
  const payables = data?.payables || [];
  const periodText = rangeLabel(from, to);
  const fileStamp = () => `Profit_Loss_${from || 'all'}_${to || toISO(new Date())}`;

  const statementRows = () =>
    statement.map((l) => ({ Particulars: l.label, 'Amount (₹)': l.kind === 'header' ? '' : l.amount, '% of Net Revenue': l.pctOfRevenue ?? '', Note: l.note || '' }));
  const monthlyRows = () =>
    monthly.map((m) => Object.fromEntries(MONTH_COLUMNS.map((c) => [c.label, m[c.key] ?? ''])));

  const runExport = (kind) => {
    setExporting(true);
    try {
      if (kind === 'excel') {
        const book = XLSX.utils.book_new();
        const head = [['Profit & Loss Statement'], ['Period', periodText], []];
        const st = XLSX.utils.sheet_add_json(XLSX.utils.aoa_to_sheet(head), statementRows(), { origin: 'A4' });
        st['!cols'] = [{ wch: 42 }, { wch: 16 }, { wch: 18 }, { wch: 60 }];
        XLSX.utils.book_append_sheet(book, st, 'P&L Statement');
        const ms = XLSX.utils.json_to_sheet(monthlyRows());
        ms['!cols'] = MONTH_COLUMNS.map(() => ({ wch: 18 }));
        XLSX.utils.book_append_sheet(book, ms, 'Monthly');
        const ps = XLSX.utils.json_to_sheet(payables.map((p) => ({ Item: p.label, 'Amount (₹)': p.amount, Note: p.note })));
        ps['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(book, ps, 'Payables');
        XLSX.writeFile(book, `${fileStamp()}.xlsx`);
      } else if (kind === 'csv') {
        downloadCsv(statementRows(), fileStamp());
      } else {
        openPrintView({
          title: 'Profit & Loss Statement',
          subtitle: `Period: ${periodText}`,
          headers: ['Particulars', 'Amount', '% of Net Revenue'],
          cells: statement.map((l) => [
            { value: l.kind === 'line' ? `    ${l.label}` : l.label, className: l.kind === 'line' ? '' : 'warn' },
            { value: l.kind === 'header' ? '' : signedInr(l.amount), className: l.kind === 'total' ? (l.amount >= 0 ? 'pos' : 'neg') : 'num' },
            { value: l.pctOfRevenue === null || l.pctOfRevenue === undefined ? '' : `${l.pctOfRevenue}%`, className: 'num' },
          ]),
        });
      }
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const netUp = (stats.netProfit || 0) >= 0;
  const cards = [
    { label: 'Net Revenue', value: inr(stats.netRevenue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.orders || 0} orders · excl. GST` },
    { label: 'Cost of Goods Sold', value: inr(stats.cogs), icon: MdInventory, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Own stock + seller payables' },
    { label: 'Gross Profit', value: signedInr(stats.grossProfit || 0), icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: stats.grossMarginPct === null || stats.grossMarginPct === undefined ? '—' : `${stats.grossMarginPct}% gross margin` },
    { label: 'Operating Expenses', value: inr(stats.opex), icon: MdReceiptLong, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Shipping + payroll' },
    {
      label: 'Net Profit',
      value: signedInr(stats.netProfit || 0),
      icon: netUp ? MdTrendingUp : MdTrendingDown,
      gradient: netUp ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#f43f5e,#dc2626)',
      sub: stats.netMarginPct === null || stats.netMarginPct === undefined ? '—' : `${stats.netMarginPct}% net margin`,
    },
    { label: 'GST Collected', value: inr(stats.gstCollected), icon: MdPercent, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: 'Payable to government' },
    { label: 'Commission Earned', value: inr(stats.commission), icon: MdAccountBalance, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: 'From seller products' },
  ];

  const pageRows = monthly.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const monthTotals = MONTH_COLUMNS.filter((c) => c.money).reduce((acc, c) => ({ ...acc, [c.key]: monthly.reduce((a, m) => a + (m[c.key] || 0), 0) }), {});

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Profit &amp; Loss Statement</Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', fontFamily: FONT }}>Period: <b>{periodText}</b></Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !data} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !data} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !data} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Period */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Period</Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={() => applyPeriod('this_fy')} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PERIODS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPeriod(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: period === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: period === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: period === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {stats.unknownCostUnits > 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontFamily: FONT }}>
          {stats.unknownCostUnits} sold unit(s) of own products have no purchase price recorded, so their cost isn't in COGS — gross and net profit are overstated until receiving records exist for them.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Statement */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Statement</Typography>
              <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>Amounts in brackets are deductions</Typography>
            </Box>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, fontFamily: FONT, color: '#64748b', fontSize: 12 }}>PARTICULARS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontFamily: FONT, color: '#64748b', fontSize: 12 }}>AMOUNT</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontFamily: FONT, color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>% OF NET REVENUE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statement.map((l, i) => {
                    const s = STATEMENT_STYLE[l.kind] || STATEMENT_STYLE.line;
                    const cellSx = { fontFamily: FONT, fontWeight: s.fontWeight, fontSize: s.fontSize, color: s.color, background: s.background, py: l.kind === 'line' ? 1 : 1.25 };
                    if (l.kind === 'header') {
                      return (
                        <TableRow key={i}>
                          <TableCell colSpan={3} sx={cellSx}>{l.label}</TableCell>
                        </TableRow>
                      );
                    }
                    const amountColor = l.kind === 'total' ? profitColor(l.amount) : l.amount < 0 ? '#b91c1c' : s.color;
                    return (
                      <TableRow key={i} hover={l.kind === 'line'}>
                        <TableCell sx={{ ...cellSx, pl: l.kind === 'line' ? 4 : 2 }}>
                          {l.label}
                          {l.note && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT, fontWeight: 500 }}>{l.note}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ ...cellSx, color: amountColor, whiteSpace: 'nowrap' }}>{signedInr(l.amount)}</TableCell>
                        <TableCell align="right" sx={{ ...cellSx, color: '#64748b', fontWeight: 600 }}>{l.pctOfRevenue === null || l.pctOfRevenue === undefined ? '' : `${l.pctOfRevenue}%`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Payables */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', p: 2.5 }}>
            <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a', mb: 2 }}>Payables &amp; Receivables</Typography>
            {payables.map((p, i) => (
              <Box
                key={p.label}
                sx={{
                  p: 1.75,
                  mb: 1.5,
                  borderRadius: '12px',
                  borderLeft: `5px solid ${['#f43f5e', '#f59e0b', '#a855f7', '#0ea5e9'][i % 4]}`,
                  background: '#f8fafc',
                }}
              >
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569', fontFamily: FONT }}>{p.label}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', fontFamily: FONT }}>{inr(p.amount)}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{p.note}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Monthly */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Month-wise Profit &amp; Loss</Typography>
            </Box>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {MONTH_COLUMNS.map((c) => (
                      <TableCell key={c.key} align={c.key === 'month' ? 'left' : 'right'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && monthly.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={MONTH_COLUMNS.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>No activity in this period.</TableCell>
                    </TableRow>
                  )}
                  {pageRows.map((m) => (
                    <TableRow key={m.month} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' } }}>
                      {MONTH_COLUMNS.map((c) => {
                        const v = m[c.key];
                        return (
                          <TableCell
                            key={c.key}
                            align={c.key === 'month' ? 'left' : 'right'}
                            sx={{ fontFamily: FONT, fontSize: 13, whiteSpace: 'nowrap', fontWeight: c.colored || c.key === 'month' ? 700 : 500, color: c.colored ? profitColor(v || 0) : '#334155' }}
                          >
                            {c.money ? signedInr(v || 0) : c.pct ? (v === null || v === undefined ? '—' : `${v}%`) : v}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {monthly.length > 0 && (
                    <TableRow sx={{ background: '#fef9c3' }}>
                      {MONTH_COLUMNS.map((c) => (
                        <TableCell key={c.key} align={c.key === 'month' ? 'left' : 'right'} sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 800, color: c.colored && c.money ? profitColor(monthTotals[c.key]) : '#0f172a', whiteSpace: 'nowrap' }}>
                          {c.key === 'month' ? 'Total' : c.money ? signedInr(monthTotals[c.key]) : c.pct ? (stats.netMarginPct === null || stats.netMarginPct === undefined ? '—' : `${stats.netMarginPct}%`) : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={monthly.length}
              page={Math.min(page, Math.max(Math.ceil(monthly.length / rowsPerPage) - 1, 0))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[6, 12, 24]}
              sx={{ borderTop: '1px solid #e2e8f0' }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfitLossReport;
